import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import MainNavigation from '../../components/ui/MainNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import DateConfirmation from './components/DateConfirmation';
import PricingSummary from './components/PricingSummary';
import PaymentForm from './components/PaymentForm';
import BookingConfirmation from './components/BookingConfirmation';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const Booking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [bookingStep, setBookingStep] = useState('payment');
  const [clientSecret, setClientSecret] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);

  const bookingInfo = location?.state?.bookingData;

  useEffect(() => {
    if (!bookingInfo) {
      navigate('/search-results');
    }
  }, [bookingInfo, navigate]);

  const trailerData = {
    id: bookingInfo?.trailerId || 1,
    title: "Heavy-Duty Enclosed Cargo Trailer",
    type: "Enclosed Trailer",
    location: "Austin, TX",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e66491e3-1767380697290.png",
    imageAlt: "Black enclosed cargo trailer with rear ramp door",
    pricing: {
      dailyRate: 75,
      weeklyDiscount: 15,
      securityDeposit: 200,
      cleaningFee: 25
    }
  };

  const calculatePricing = () => {
    if (!bookingInfo?.startDate || !bookingInfo?.endDate) return null;

    const start = new Date(bookingInfo.startDate);
    const end = new Date(bookingInfo.endDate);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

    let subtotal = days * trailerData?.pricing?.dailyRate;
    let discount = 0;

    if (days >= 7) {
      discount = subtotal * (trailerData?.pricing?.weeklyDiscount / 100);
    }

    const total = subtotal - discount + trailerData?.pricing?.securityDeposit + trailerData?.pricing?.cleaningFee;

    return {
      days,
      subtotal,
      discount,
      total,
      dailyRate: trailerData?.pricing?.dailyRate,
      weeklyDiscount: trailerData?.pricing?.weeklyDiscount,
      securityDeposit: trailerData?.pricing?.securityDeposit,
      cleaningFee: trailerData?.pricing?.cleaningFee
    };
  };

  const pricing = calculatePricing();

  const handlePaymentSuccess = (paymentIntent) => {
    setPaymentIntentId(paymentIntent?.id);
    setBookingData({
      ...bookingInfo,
      trailer: trailerData,
      pricing,
      paymentIntentId: paymentIntent?.id,
      bookingId: `BK${Date.now()}`
    });
    setBookingStep('confirmation');
  };

  const breadcrumbs = [
  { label: 'Home', path: '/home-page' },
  { label: 'Browse Trailers', path: '/search-results' },
  { label: 'Booking', path: '/booking' }];


  if (!bookingInfo || !pricing) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbNavigation items={breadcrumbs} />

          <div className="mt-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
              {bookingStep === 'payment' ? 'Complete Your Booking' : 'Booking Confirmed!'}
            </h1>
            <p className="font-body text-base md:text-lg text-muted-foreground">
              {bookingStep === 'payment' ? 'Review your rental details and complete payment' : 'Your trailer rental has been successfully confirmed'}
            </p>
          </div>

          {bookingStep === 'payment' ?
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <DateConfirmation
                trailer={trailerData}
                startDate={bookingInfo?.startDate}
                endDate={bookingInfo?.endDate}
                days={pricing?.days} />
              

                <div className="bg-card rounded-lg shadow-elevation-3 p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="CreditCard" size={20} color="var(--color-primary)" strokeWidth={2} />
                    </div>
                    <h2 className="font-heading text-2xl font-semibold text-foreground">
                      Payment Information
                    </h2>
                  </div>

                  {clientSecret ?
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                      <PaymentForm
                    clientSecret={clientSecret}
                    onSuccess={handlePaymentSuccess}
                    amount={pricing?.total}
                    onCreatePaymentIntent={setClientSecret}
                    bookingInfo={bookingInfo}
                    trailerData={trailerData} />
                  
                    </Elements> :

                <PaymentForm
                  onCreatePaymentIntent={setClientSecret}
                  amount={pricing?.total}
                  bookingInfo={bookingInfo}
                  trailerData={trailerData}
                  clientSecret={clientSecret}
                  onSuccess={handlePaymentSuccess} />

                }
                </div>
              </div>

              <div className="lg:col-span-1">
                <PricingSummary pricing={pricing} />
              </div>
            </div> :

          <BookingConfirmation bookingData={bookingData} />
          }
        </div>
      </div>
    </div>);

};

export default Booking;